import { useRef } from 'react';
import { useLingui } from '@lingui/react/macro';
import { motion, animate, useMotionValue, useTransform, useAnimationFrame, useReducedMotion } from 'motion/react';
import type { PanInfo } from 'motion/react';
import { useIsMobile } from '@worldcoo/ui/hooks/use-mobile';
import { cn } from '@worldcoo/ui/lib/utils';
import { Image } from '@/routes/-components/image';

/**
 * The section's glyph, from the hand-off. Declared next to its only consumer, the way the footer
 * declares its LinkedIn badge — `icons.tsx` is the language selector's flags.
 */
function CoinArrowOut(props: { className?: string }) {
	return (
		<svg
			viewBox="0 0 32 32"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.5}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
			{...props}
		>
			<path d="M12.6667 18.3334C12.6667 19.6267 13.6668 20.6667 14.8934 20.6667H17.4001C18.4667 20.6667 19.3334 19.7601 19.3334 18.6267C19.3334 17.4134 18.8001 16.9734 18.0134 16.6934L14.0001 15.2934C13.2134 15.0134 12.6801 14.5867 12.6801 13.3601C12.6801 12.2401 13.5467 11.3201 14.6134 11.3201H17.1201C18.3467 11.3201 19.3468 12.3601 19.3468 13.6534" />
			<path d="M16 10V22" />
			<path d="M29.3334 16.0001C29.3334 23.3601 23.3601 29.3334 16.0001 29.3334C8.64008 29.3334 2.66675 23.3601 2.66675 16.0001C2.66675 8.64008 8.64008 2.66675 16.0001 2.66675" />
			<path d="M29.3333 8.00008V2.66675H24" />
			<path d="M22.6667 9.33341L29.3334 2.66675" />
		</svg>
	);
}

/**
 * Payment gateways and technology partners we integrate with. Files are the ones `www` uses, untouched.
 *
 * All sixteen carry an opaque white plate, dropped at render time with `mix-blend-multiply` rather than
 * by editing the files — the CDN derivative URL is keyed on the source path, not its contents, so an
 * edit would keep serving the old crop until someone purged it. THIS DEPENDS ON THE TILE STAYING LIGHT;
 * on a dark one multiply would swallow the logos whole.
 *
 * They are 125x55 legacy rasters, which is why the fine print reads soft. Only real SVGs fix that.
 */
const TECH_PARTNERS: { file: string; name: string }[] = [
	{ file: 'sabadell', name: 'Banco Sabadell' },
	{ file: 'paytef', name: 'PayTEF' },
	{ file: 'mastercard', name: 'Mastercard' },
	{ file: 'adyen', name: 'Adyen' },
	{ file: 'comercia-global-payments', name: 'Comercia Global Payments' },
	{ file: 'global-payments', name: 'Global Payments' },
	{ file: 'wordline', name: 'Worldline' },
	{ file: 'ingenico-group', name: 'Ingenico' },
	{ file: 'necomplus', name: 'Necomplus' },
	{ file: 'sipay', name: 'Sipay' },
	{ file: 'ncr', name: 'NCR' },
	{ file: 'diebold-nixford', name: 'Diebold Nixdorf' },
	{ file: 'clearone', name: 'ClearOne' },
	{ file: 'ecopaynet', name: 'Ecopaynet' },
	{ file: 'saima-systems', name: 'Saima Systems' },
	{ file: 'sistemas-avanzados-en-monetica', name: 'Sistemas Avanzados en Monética' },
];

// Tile geometry, applied as inline styles because it drives both the layout and the wrap maths: a
// Tailwind class that drifted from `TILE_H` would desync the loop and show a seam.
const TILE_W = 190;
const TILE_H = 96;
const TILE_GAP = 20;
const ITEM_H = TILE_H + TILE_GAP;
/**
 * Sized to the pixels that exist: the files are 125x55, so 55 is the tallest a mark renders without the
 * browser inventing detail. imgproxy never enlarges (`generateImagePath` passes no `enlarge`), so
 * asking for more just stretches the same bitmap.
 */
const LOGO_BOX_W = TILE_W - 24;
const LOGO_H = 55;
const COLUMN_H = 560;
/** px per millisecond. 26px/s, the speed the CSS version drifted at. */
const SPEED = 0.026;

/**
 * One column of logos on an endless belt: two copies of the list with the offset wrapped into
 * `(-listH, 0]`, seamless as long as one copy is taller than the column (8 tiles = 1056px against 560).
 *
 * Dragging is off on phones, where the `touch-none` it needs would swallow the page's vertical swipe.
 */
function LogoColumn(props: { logos: typeof TECH_PARTNERS; dir: 1 | -1; className?: string }) {
	const reduce = useReducedMotion() ?? false;
	const isMobile = useIsMobile();
	const listH = props.logos.length * ITEM_H;

	const pos = useMotionValue(0);
	const interacting = useRef(false);
	const inertia = useRef<{ stop: () => void } | null>(null);
	const y = useTransform(pos, (v) => -(((v % listH) + listH) % listH));

	useAnimationFrame((_, delta) => {
		if (reduce || interacting.current) return;
		pos.set(pos.get() + props.dir * delta * SPEED);
	});

	const draggable = !reduce && !isMobile;
	const handlers = draggable
		? {
				onPanStart: () => {
					interacting.current = true;
					inertia.current?.stop();
				},
				onPan: (_: PointerEvent, info: PanInfo) => pos.set(pos.get() - info.delta.y),
				onPanEnd: (_: PointerEvent, info: PanInfo) => {
					inertia.current = animate(pos, pos.get(), {
						type: 'inertia',
						velocity: -info.velocity.y,
						power: 0.7,
						timeConstant: 420,
						onComplete: () => {
							interacting.current = false;
						},
					});
				},
			}
		: {};

	return (
		<motion.div
			style={{ height: COLUMN_H, width: TILE_W }}
			className={cn(
				'overflow-hidden [mask-image:linear-gradient(to_bottom,transparent,black_18%,black_82%,transparent)]',
				draggable && 'cursor-grab touch-none select-none active:cursor-grabbing',
				props.className,
			)}
			{...handlers}
		>
			<motion.div className="flex flex-col" style={{ y }}>
				{[...props.logos, ...props.logos].map((partner, i) => (
					<div
						// oxlint-disable-next-line react/no-array-index-key
						key={i}
						style={{ height: TILE_H, marginBottom: TILE_GAP }}
						className="bg-ink/5 isolate flex shrink-0 items-center justify-center rounded-lg px-3"
					>
						<Image
							src={`/home/tech-partners/${partner.file}.png`}
							alt={partner.name}
							height={LOGO_H}
							aspectRatio={LOGO_BOX_W / LOGO_H}
							objectFit="contain"
							className="w-full object-contain! mix-blend-multiply"
						/>
					</div>
				))}
			</motion.div>
		</motion.div>
	);
}

// Mobile row geometry. px per second, matching the merchant wheel's mobile rows so both logo walls on
// the page read as one motion.
const MOBILE_SPEED = 32;
const MOBILE_TILE_W = 150;
const MOBILE_TILE_H = 76;
const MOBILE_GAP = 12;

/**
 * Below `xl` the belts become two horizontal marquees, the same shape the merchant wheel takes there.
 * A vertical column on a phone could only show one of the two, and dragging is off at that size.
 *
 * Duration is DERIVED from the number of logos: `translateX(-50%)` of a duplicated list travels one
 * copy's width, so a fixed duration would make the speed depend on how many partners there are. The
 * class keeps a duration anyway, so reduced-motion handling and the fallback stay in CSS.
 */
function LogoRowMobile(props: { logos: typeof TECH_PARTNERS; reverse?: boolean }) {
	const seconds = Math.round((props.logos.length * (MOBILE_TILE_W + MOBILE_GAP)) / MOBILE_SPEED);
	return (
		<div className="overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
			<div
				style={{ animationDuration: `${seconds}s` }}
				className={cn(
					'flex w-max',
					props.reverse
						? 'motion-safe:animate-[new-marquee_32s_linear_infinite_reverse]'
						: 'motion-safe:animate-[new-marquee_32s_linear_infinite]',
				)}
			>
				{[...props.logos, ...props.logos].map((partner, i) => (
					<div
						// oxlint-disable-next-line react/no-array-index-key
						key={i}
						style={{ width: MOBILE_TILE_W, height: MOBILE_TILE_H, marginRight: MOBILE_GAP }}
						className="bg-ink/5 isolate flex shrink-0 items-center justify-center rounded-lg px-3"
					>
						<Image
							src={`/home/tech-partners/${partner.file}.png`}
							alt={partner.name}
							height={LOGO_H}
							aspectRatio={(MOBILE_TILE_W - 24) / LOGO_H}
							objectFit="contain"
							className="w-full object-contain! mix-blend-multiply"
						/>
					</div>
				))}
			</div>
		</div>
	);
}

/** "Cómo lo hacemos posible" — two belts drifting in opposite directions, faded out top and bottom. */
export function TechPartners() {
	const { t } = useLingui();
	const half = Math.ceil(TECH_PARTNERS.length / 2);

	return (
		<section className="overflow-hidden bg-white">
			<div className="mx-auto grid max-w-[1440px] items-center gap-12 px-6 py-16 md:py-20 lg:grid-cols-[minmax(0,1fr)_auto] xl:px-10">
				<div className="flex flex-col items-start gap-6">
					<div className="relative">
						{/* Tilted orange plate peeking from behind the icon, as in the hand-off. */}
						<div
							aria-hidden
							className="bg-brand absolute inset-0 translate-x-1 translate-y-1 rotate-[10deg] rounded-lg"
						/>
						<div className="bg-ink relative grid size-16 place-items-center rounded-lg text-white">
							<CoinArrowOut className="size-8" />
						</div>
					</div>
					<div className="flex flex-col gap-2">
						<h2 className="text-3xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
							{t`How we make it possible`}
						</h2>
						<p className="text-ink max-w-[533px] text-base leading-[1.5]">
							{t`We hold partnership agreements with the main payment gateways on the market and with technology partners, so the integration is native and secure.`}
						</p>
					</div>
				</div>

				<div className="hidden justify-center gap-5 xl:flex">
					<LogoColumn logos={TECH_PARTNERS.slice(0, half)} dir={1} />
					<LogoColumn logos={TECH_PARTNERS.slice(half)} dir={-1} />
				</div>

				{/* Split by index rather than in half, so neither row ends up holding all the wide wordmarks. */}
				<div className="flex flex-col gap-3 xl:hidden">
					<LogoRowMobile logos={TECH_PARTNERS.filter((_, i) => i % 2 === 0)} />
					<LogoRowMobile logos={TECH_PARTNERS.filter((_, i) => i % 2 === 1)} reverse />
				</div>
			</div>
		</section>
	);
}
